/**
 * Map Tool type contract — the extensibility seam for the map toolbox.
 *
 * `MapToolbox` is a dumb renderer over a registry of these descriptors.
 * Adding a future tool (manual pinning, CSV upload, …) requires only appending
 * a `MapTool` descriptor + its panel component — no edits to the rail, the
 * surface, or `MapShell`.
 */
import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { MapShellRenderState } from '@/features/locations/components/MapShell';

/** Hint shown on a workspace-gated tool trigger when no workspace is selected. */
export const WORKSPACE_REQUIRED_HINT = 'Select or create a workspace';

/** Hint shown on a `blockedWhileComputing` tool trigger while a compute run is active. */
export const COMPUTING_HINT = 'Wait for the current location to finish computing';

/** Context handed to a tool's `renderPanel` while it is armed. */
export interface ToolPanelContext {
  /** Shell state from MapShell's `children` render-prop (provides the live map ref). */
  shell: MapShellRenderState;
  /** Disarm this tool and run its cleanup (wired to ✕ / Escape by the rail). */
  close: () => void;
}

/** A static descriptor registered in `MAP_TOOLS`. */
export interface MapTool {
  /** Stable unique key (React key + `armedToolId`), e.g. `'driving-time'`. */
  id: string;
  /** Rail button `aria-label` / tooltip. */
  label: string;
  /** Rail glyph. */
  icon: LucideIcon;
  /** Gating: `true` → disabled + tooltip when no active workspace. */
  requiresWorkspace: boolean;
  /** Gating: `true` → disabled + tooltip while a compute run is active. */
  blockedWhileComputing?: boolean;
  /** Contextual panel rendered while this tool is armed. */
  renderPanel: (ctx: ToolPanelContext) => ReactNode;
}
